import api from "../../../core/api";
import {
  ChangeMachine,
  DecommissionPreview,
  Machine,
  MachineType,
  MachinesQuery,
  ManufacturerReference,
  OperationType,
  AxisConfiguration,
  ControlSystemBrand,
  ControlSystemModel,
  AutomationLevel,
  ReportProblemRequest,
  ReportProblemResponse,
} from "../models/models";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.machineryApiBaseUrl };
}

// --- MACHINE-DELETE (Admin hard delete of a decommissioned machine) ---
//
// CONTRACT: both endpoints carry the SAME body — the eligibility GET on
// success, and the permanent DELETE on the refusal path where the server re-runs
// its own check. One shape, one client-side mapper
// (v2/fleet/machineDeleteBlockers.ts). Per MACHINE_DELETE_SPEC.md §4.1 / §4.2,
// confirmed against the implementation by the BE↔FE reconciliation pass.
//
// TWO NON-2xx RESPONSES ARE HANDLED AS DATA RATHER THAN AS FAILURES — 409
// (refused; carries the enumerated blockers) and 429 (rate-limited; EMPTY
// body). Both would otherwise be flattened by the shared interceptor, so BOTH
// endpoints below opt them back in via validateStatus; see the note on each
// method. 401 / 403 / 404 / 5xx still reject through the interceptor unchanged,
// so global logout and access-denied handling are untouched.
//
// `code` is a CLOSED set of eight values. It is typed as a union on purpose: a
// ninth code added to the spec breaks the exhaustive message map at compile
// time instead of silently rendering nothing.
//
// `count` is sent only where a count is meaningful; `service` only on
// VerificationUnavailable.
export type MachineDeleteBlockerCode =
  | "NotDecommissioned"
  | "HasServiceReports"
  | "HasDocuments"
  | "HasNameplate"
  | "HasServiceRequests"
  | "HasWorkOrders"
  | "HasGatewayConnection"
  | "VerificationUnavailable";

export interface MachineDeleteBlocker {
  code: MachineDeleteBlockerCode;
  count?: number;
  /** VerificationUnavailable only: Servicing | Maintenance | MachineGateway. */
  service?: string;
}

export interface MachineDeleteEligibility {
  machineId: string;
  /**
   * SENT BY THE SERVER, DELIBERATELY NEVER READ — do not "fix" this.
   *
   * The server fills it from `machine.Model` alone. Every v2 surface names the
   * machine with `getMachineDisplayName(machine)` (shared/utils/machineUtils
   * .ts:22-25), which is brand + model — the same string the card, the row, the
   * profile hero and the breadcrumb already show. Switching the confirm dialog
   * and the success toast to this field would silently drop the brand from the
   * two places where naming the RIGHT machine matters most, and would make them
   * disagree with the page the user is looking at. The field stays in the type
   * because it is on the wire; it is not a value this client consumes.
   */
  machineName?: string;
  canDelete: boolean;
  blockers?: MachineDeleteBlocker[];
}

/** Outcome of the eligibility read. A `kind` union rather than booleans: there
 *  are three outcomes and two of them carry no eligibility payload at all. */
export type MachineDeleteEligibilityResponse =
  | { kind: "eligibility"; eligibility: MachineDeleteEligibility }
  | { kind: "throttled" };

export type MachineDeleteResponse =
  | { kind: "deleted" }
  | { kind: "refused"; eligibility: MachineDeleteEligibility }
  | { kind: "throttled" };

class CustomerMachineryApi {
  getMachines(query: MachinesQuery): Promise<Record<string, Machine[]>> {
    return api.get<Record<string, Machine[]>>(
      `/v1/customer/machines`, { ...getRequestConfig(), params: query, paramsSerializer: { indexes: null } })
      .then(x => x.data);
  }

  getMachine(id: string): Promise<Machine> {
    return api.get<Machine>(
        `/v1/customer/machines/${id}`, { ...getRequestConfig() })
        .then(x => x.data);
  }

  createMachine(model: ChangeMachine): Promise<string> {
    return api.post<string>(`/v1/customer/machines`, model, getRequestConfig()).then(x => x.data);
  }

  changeMachine(id: string, model: ChangeMachine) {
    return api.put<any>(`/v1/customer/machines/${id}`, model, getRequestConfig());
  }

  deleteMachine(id: string): Promise<any> {
    return api.delete(`/v1/customer/machines/${id}`, getRequestConfig());
  }

  getTypes(): Promise<MachineType[]> {
    return api.get<MachineType[]>(`/v1/customer/machines/types`, getRequestConfig())
      .then(x => x.data);
  }

  getManufacturersById(machineTypeId: string): Promise<ManufacturerReference[]> {
    return api.get<ManufacturerReference[]>(`/v1/customer/machines/types/${machineTypeId}/manufacturers`, getRequestConfig())
      .then(x => x.data);
  }

  getOperationTypes(machineTypeId: string): Promise<OperationType[]> {
    return api.get<OperationType[]>(`/v1/customer/machines/types/${machineTypeId}/operation-types`, getRequestConfig())
      .then(x => x.data);
  }

  getAxisConfigurations(operationTypeId: string): Promise<AxisConfiguration[]> {
    return api.get<AxisConfiguration[]>(`/v1/customer/machines/operation-types/${operationTypeId}/axis-configurations`, getRequestConfig())
      .then(x => x.data);
  }

  getControlSystemBrands(machineTypeId: string): Promise<ControlSystemBrand[]> {
    return api.get<ControlSystemBrand[]>(`/v1/customer/machines/types/${machineTypeId}/control-system-brands`, getRequestConfig())
      .then(x => x.data);
  }

  getControlSystemModels(controlSystemId: string): Promise<ControlSystemModel[]> {
    return api.get<ControlSystemModel[]>(`/v1/customer/machines/control-systems/${controlSystemId}/models`, getRequestConfig())
      .then(x => x.data);
  }

  getAutomationLevels(): Promise<AutomationLevel[]> {
    return api.get<AutomationLevel[]>(`/v1/customer/machines/automation-levels`, getRequestConfig())
      .then(x => x.data);
  }

  // --- Decommission ---

  getDecommissionPreview(id: string): Promise<DecommissionPreview> {
    return api.get<DecommissionPreview>(
      `/v1/customer/machines/${id}/decommission-preview`, getRequestConfig()
    ).then(x => x.data);
  }

  decommissionMachine(id: string, data?: { reason?: string; forceArchiveRfs?: boolean }): Promise<void> {
    return api.delete(`/v1/customer/machines/${id}`, { ...getRequestConfig(), data }).then(() => {});
  }

  restoreMachine(id: string): Promise<void> {
    return api.post(`/v1/customer/machines/${id}/restore`, null, getRequestConfig()).then(() => {});
  }

  getDecommissionedMachines(): Promise<Machine[]> {
    return api.get<Machine[]>(
      `/v1/customer/machines/decommissioned`, getRequestConfig()
    ).then(x => x.data);
  }

  // --- Report Problem ---

  reportProblem(machineId: string, model: ReportProblemRequest): Promise<ReportProblemResponse> {
    return api.post<ReportProblemResponse>(
      `/v1/customer/machines/${machineId}/report-problem`, model, getRequestConfig()
    ).then(x => x.data);
  }

  // --- Machine hard delete (spec §4.1/§4.2) ---

  // Admin-only. Fetched WHEN THE USER OPENS THE DELETE FLOW, never on page load
  // and never to decide whether to render the action — the button is gated on
  // usePermission(Machinery).canAdmin, which costs no request.
  //
  // `signal` is REQUIRED of the caller, not optional: this request fans out to
  // three peer services server-side, and an un-aborted hang leaves the v2 delete
  // flow busy for the page's lifetime (review finding 2).
  //
  // 429 IS DATA, NOT A FAILURE. Verified against the shipped controller:
  // [EnableRateLimiting(EligibilityPolicyName)] on this action
  // (CustomerMachinesController.cs:395), policy "machine-delete-eligibility",
  // partitioned per authenticated user with an IP fallback
  // (MachineDeleteRateLimiter.cs:89-114).
  //
  // ITS LIMIT IS 20/min BY DEFAULT AND IS CONFIG-OVERRIDABLE — not a fixed
  // fact. `DefaultEligibilityPerMinute = 20` (MachineDeleteRateLimiter.cs:33) is
  // the FALLBACK; a deployed environment can set
  // `MachineDelete:RateLimit:EligibilityPerMinute` to anything (read at :40-41,
  // fail-closed to the default on missing/non-positive). Do not treat the 20 as
  // the deployed number when reading a support report.
  //
  // The rejection is 429 with NO body, and that is now guaranteed BY THIS
  // ENDPOINT'S OWN POLICY: the limiter implements IRateLimiterPolicy<string>
  // with a per-policy OnRejected that assigns Status429TooManyRequests
  // (MachineDeleteRateLimiter.cs:83-87), which runs AFTER the middleware has
  // applied the shared RejectionStatusCode and therefore wins whatever the
  // shared value is (reasoning at :70-72). The shared assignment in
  // NameplateRateLimiter.cs:47 still exists and still says 429, but these two
  // endpoints no longer depend on it — which matters, because ASP.NET's own
  // default is 503 and a 503 here would miss the branch below and show a
  // throttled user a wrong-cause message.
  //
  // The empty body is exactly why this needs validateStatus: the shared
  // interceptor (core/api.ts:72-77) reads only `response.data.message` /
  // `.errorCode` / `.code`, so a 429 would reach the caller as
  // `{message: undefined, code: undefined}` — indistinguishable from a 500. The
  // caller would then tell a throttled user that the CHECK failed and invite the
  // retry that keeps them throttled.
  getMachineDeleteEligibility(
    id: string,
    signal: AbortSignal
  ): Promise<MachineDeleteEligibilityResponse> {
    return api
      .get<MachineDeleteEligibility>(`/v1/customer/machines/${id}/delete-eligibility`, {
        ...getRequestConfig(),
        signal,
        validateStatus: (s: number) => (s >= 200 && s < 300) || s === 429,
      })
      .then((x) =>
        x.status === 429
          ? ({ kind: "throttled" } as MachineDeleteEligibilityResponse)
          : ({ kind: "eligibility", eligibility: x.data } as MachineDeleteEligibilityResponse)
      );
  }

  // 409 IS TREATED AS A RESOLVED RESPONSE, DELIBERATELY. That reasoning is about
  // OUR interceptor, not the server: core/api.ts:72-77 rebuilds every non-bot
  // rejection as `{ message, code }` taken from `error.response.data.message` /
  // `.errorCode` / `.code` and rejects with THAT object — `response`, and with
  // it the enumerated `blockers` array, is dropped before any caller sees it.
  // Per-request `validateStatus` keeps the body readable without editing the
  // shared interceptor. Exactly the pattern shipped for LOC-DELETE
  // (api/customerLocationsApi.ts:99-123).
  //
  // 429 IS HANDLED HERE TOO — this endpoint is rate limited as well, under its
  // OWN policy "machine-delete-permanent" (CustomerMachinesController.cs:422),
  // tighter than the eligibility GET's. 5/min BY DEFAULT and config-overridable
  // exactly like the GET: `DefaultPermanentDeletePerMinute = 5`
  // (MachineDeleteRateLimiter.cs:35) is only the fallback for
  // `MachineDelete:RateLimit:PermanentDeletePerMinute` (:42-44), so treat the 5
  // as a default, never as the deployed number. The same per-policy OnRejected
  // pins the status to 429 (:83-87).
  //
  // WHY it needs its own limiter is argued where the policy is defined
  // (MachineDeleteRateLimiter.cs:12-17) and deliberately not restated here — a
  // rationale duplicated in a client file drifts out of sync with the decision.
  //
  // 401 / 403 / 404 / 5xx still reject through the interceptor unchanged, so
  // the global logout and access-denied handling are untouched.
  deleteMachinePermanently(
    id: string,
    signal: AbortSignal
  ): Promise<MachineDeleteResponse> {
    return api
      .delete(`/v1/customer/machines/${id}/permanent`, {
        ...getRequestConfig(),
        signal,
        validateStatus: (s: number) => (s >= 200 && s < 300) || s === 409 || s === 429,
      })
      .then((x) => {
        if (x.status === 429) return { kind: "throttled" } as MachineDeleteResponse;
        if (x.status !== 409) return { kind: "deleted" } as MachineDeleteResponse;
        // Defensive: a 409 body is JSON, but tolerate a string body (wrong
        // content-type) so the enumerated reasons still reach the caller.
        let data: unknown = x.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            data = {};
          }
        }
        return {
          kind: "refused",
          eligibility: (data || {}) as MachineDeleteEligibility,
        } as MachineDeleteResponse;
      });
  }
}

export default new CustomerMachineryApi();
